// ── 출퇴근 달력 윗부분 (2026-09-29 사용자 그림 구조 — 직원별 상세 탭과 같은 틀) ─────────────
//   AttendancePageHeader 틀로 그린다:
//     1줄: 제목·설명 ............ [달력|직원별 상세] [☰ 메뉴: 예외 스케줄 등록·시간 설정·엑셀 다운로드]
//     2줄: [이름 검색] [필터 버튼(부서·상태)]
//     3줄: [월별|주별] ........ [◀ 2026년 9월 ▶][오늘] ........ [범례 색칩]
//   주별이면 3줄 가운데가 주 넘기기(◀ 이전 주 · 기간 · 다음 주 ▶ · 오늘)로 바뀐다.
//   날짜 글자는 줄 정확히 한가운데, [오늘]은 그 오른쪽 옆(2026-09-30).
//   휴대폰은 3줄이 세로로 쌓이고 범례도 가운데.
//   예전에는 제목 줄에 월별/주별·달 넘기기가, 그 아래 줄에 부서·이름·상태 칸과 범례가 있었다.
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addDays } from './utils';
import { StatusBadge } from './StatusBadge';
import AttendancePageHeader from '../attendancehub/AttendancePageHeader';
// 달 넘기기·[오늘]·가운데 줄 틀은 직원별 상세 탭과 같이 쓴다(2026-10-13, 모양 그대로 옮김)
import AttendanceMonthNav, { NavCenterRow, TodayButton, navSmallBtn } from '../attendancehub/AttendanceMonthNav';
import { stdTabGroup, stdTabBtn } from '../adminlayout/designStandard';

// 상태 범례에 표시할 상태 코드 목록 (offday/scheduled는 제외) — 예전 FilterBar 와 같다
const LEGEND_STATUSES = ['present', 'missing_out', 'absent', 'on_leave', 'half_leave', 'offday_work', 'holiday'];

/**
 * @param {object} props
 * @param {boolean} props.isMobile
 * @param {string}  props.title / props.subtitle   제목·설명 (탭 화면이면 "출퇴근 관리")
 * @param {ReactNode} props.belowTitle             제목 아래 칸(지금은 비움 — 통합 메뉴 회사 칩은 필터 창 안으로, 2026-09-30)
 * @param {ReactNode} props.menu                   ☰ 메뉴(AttendanceMenu) — 항목은 AttendanceCalendar 가 정한다
 * @param {ReactNode} props.tabSlot                [달력|직원별 상세]
 * @param {string}   props.filterName / onFilterNameChange   이름 검색
 * @param {ReactNode} props.filterButton           필터 버튼(부서·상태)
 * @param {'month'|'week'} props.viewMode / onViewModeChange
 * @param {number}   props.year / props.month / onMoveMonth(delta)
 * @param {string}   props.weekStart / onSetWeekStart / onResetToToday
 */
export function ControlBar({
  isMobile, title = '출퇴근 달력', subtitle = '출퇴근 현황을 달력으로 봅니다.', belowTitle = null,
  menu = null, tabSlot = null,
  filterName, onFilterNameChange, filterButton = null,
  viewMode, onViewModeChange, year, month, onMoveMonth,
  weekStart, onSetWeekStart, onResetToToday,
}) {
  const weekNav = (
    <>
      <button type="button" onClick={() => onSetWeekStart((prev) => addDays(prev, -7))} style={navSmallBtn}>
        <ChevronLeft size={14} aria-hidden="true" /> 이전 주
      </button>
      <span style={{ fontSize: 16, fontWeight: 800, minWidth: 180, textAlign: 'center' }}>
        {weekStart} ~ {addDays(weekStart, 6)}
      </span>
      <button type="button" onClick={() => onSetWeekStart((prev) => addDays(prev, 7))} style={navSmallBtn}>
        다음 주 <ChevronRight size={14} aria-hidden="true" />
      </button>
    </>
  );
  const todayBtn = <TodayButton onClick={onResetToToday} />;
  const legend = LEGEND_STATUSES.map((s) => <StatusBadge key={s} status={s} small />);

  // 날짜 글자를 줄 정확히 한가운데에 (2026-09-30 휴대폰: 사용자 "상단이 왼쪽으로 치우쳐보여" / PC: 같은 날 "3번 해줘").
  //   [오늘]까지 한 묶음으로 가운데에 두면 글자가 오늘 폭만큼 왼쪽으로 밀린다(23).
  //   그래서 빈칸 · 날짜 넘기기 · 오늘 3칸으로 나눈다. 양쪽 칸이 같은 폭(1fr)이라 가운데 칸이 한가운데에 오고, 오늘은 그 오른쪽 옆.
  //   휴대폰은 줄 폭 전체를 쓰고, PC 는 3줄 가운데 칸 안에서 양쪽 칸이 오늘 폭만큼씩 잡힌다.
  //   좁은 폰에서 오늘이 칸보다 넓으면 칸이 늘어 몇 px 밀릴 뿐 넘치지 않는다.
  //   휴대폰 주별은 오늘이 이미 아랫줄로 내려가 날짜 줄이 한가운데라 그대로.
  //   월별 [‹ 2026년 10월 ›][오늘] 은 직원별 상세 탭과 같은 부품(AttendanceMonthNav, 2026-10-13).
  let center;
  if (viewMode !== 'week') {
    center = <AttendanceMonthNav isMobile={isMobile} year={year} month={month} onMoveMonth={onMoveMonth} onToday={onResetToToday} />;
  } else if (!isMobile) {
    center = <NavCenterRow isMobile={false} todayButton={todayBtn}>{weekNav}</NavCenterRow>;
  } else {
    center = (
      <>
        {weekNav}
        {todayBtn}
      </>
    );
  }

  return (
    <AttendancePageHeader
      isMobile={isMobile}
      title={title}
      subtitle={subtitle}
      belowTitle={belowTitle}
      tabSlot={tabSlot}
      menu={menu}
      searchValue={filterName}
      onSearchChange={onFilterNameChange}
      filterButton={filterButton}
      // 3줄 (2026-09-29 사용자 그림 3차): 왼쪽 [월별|주별] · 가운데 [◀ 2026년 9월 ▶][오늘] · 오른쪽 범례
      toolbarLeft={(
        <div className="no-print" style={stdTabGroup}>
          <button type="button" onClick={() => onViewModeChange('month')} style={stdTabBtn(viewMode === 'month')}>월별</button>
          <button type="button" onClick={() => onViewModeChange('week')} style={stdTabBtn(viewMode === 'week')}>주별</button>
        </div>
      )}
      toolbarCenter={center}
      // 휴대폰은 범례를 가운데로 모은다(2026-09-30 사용자). 틀(AttendancePageHeader)은 직원별 상세 탭과 같이 쓰니
      //   틀은 두고 여기서만 감싼다 — 상세 탭 상태 버튼 줄은 그대로.
      toolbarRight={isMobile ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 6 }}>{legend}</div>
      ) : legend}
    />
  );
}
